import React, { useEffect, useRef, useState } from "react";
import { useFetchData as usePagination } from "../../../../../hooks/useFetch/pagination";
import { App, Spin } from "antd";
import type { ColumnsType } from "antd/es/table";
import { StyledSourceValuesTableWrapper, StyledTable } from "../../analysisAgent.style";
import { SpinWrapper } from "../../../../../app/app.style";
import {
  useFetchData as useSubmitManyValues,
  useFetchData as useValidateManyValues,
} from "../../../../../hooks/useFetch/post";
import { LoadingOutlined } from "@ant-design/icons";
import { useAnalysisAgentContext } from "../../_analysisAgentContext";
import SourceTag from "../../../../masterComponents/SourceTag";
import SubmitActions from "../widgets/SubmitActions";
import { getBatchField } from "../../../../../entities/entities";
import type { TableRowSelection } from "antd/es/table/interface";
import type { ReviewSourceValuesTableType } from "./types/ReviewSourceValuesTableType";
import AgentTableToolbar from "../widgets/AgentTableToolbar";
import type { ToolbarPendingBtn } from "../widgets/types/AgentTableToolbarType";

const ReviewSourceValuesTable: React.FC<ReviewSourceValuesTableType> = ({
  fetchAttributeStatistics,
  currentBatch,
  disabled,
}) => {
  const { message: messageApi } = App.useApp();

  const [pageSize, setPageSize] = useState(25);
  const [selectedCheckboxKeys, setSelectedCheckboxKeys] = useState([]);
  const [dataForActionWithManyValues, setDataForActionWithManyValues] =
    useState([]);
  const [pendingBtn, setPendingBtn] = useState<ToolbarPendingBtn>(null);

  const { attributeToAnalyze, parentRowSourceValuesParams, activeBatch } =
    useAnalysisAgentContext();

  const curParentBatch =
    parentRowSourceValuesParams?.targetTable === "parent"
      ? currentBatch
      : activeBatch;

  const [
    fetchValuesFromHash,
    {
      isLoading: isLoadingValuesFromHashData,
      data: valuesFromHashData,
      totalRows: valuesFromHashTableTotalRows,
      page,
      error: valuesFromHashError,
      loadMore: valuesFromHashLoadMore,
      clearError: clearValuesFromHashError,
    },
  ] = usePagination<any[]>({
    optionalArray: [
      parentRowSourceValuesParams?.hash,
      parentRowSourceValuesParams?.targetTable,
    ],
    perPage: pageSize,
    query: "/statistics/attributes/@attributeName/values/paginated",
    selectData: (data: { items?: any[] | null }) =>
      (data?.items ?? []).map((v) => {
        return {
          id: v._id,
          tag: v.tag,
          class: v.class_name,
          description: v.description,
          jde: v["llm-extractions"].FULL_JDE_EQUIPMENTS,
          ep: v["llm-extractions"].EP_EQUIPMENTS,
          spi: v["llm-extractions"].SPI_EQUIPMENTS,
          final: v.finals,
        };
      }),
    selectPageInfo: (data) => data.page_info,
  });

  useEffect(() => {
    if (valuesFromHashError) {
      messageApi.error(valuesFromHashError.message);
      clearValuesFromHashError?.();
    }
  }, [valuesFromHashError, messageApi]);

  const sourceValuesTableRef = useRef(null);

  useEffect(() => {
    if (
      parentRowSourceValuesParams?.hash &&
      parentRowSourceValuesParams?.targetTable
    ) {
      fetchValuesFromHash(
        { attributeName: attributeToAnalyze },
        { hash: parentRowSourceValuesParams.hash },
      );
    }
  }, [
    parentRowSourceValuesParams?.hash,
    parentRowSourceValuesParams?.targetTable,
    pageSize,
  ]);

  const [
    fetchSubmitManyValuesById,
    {
      isLoading: isLoadingSubmitManyValuesById,
      data: submitManyValuesByIdData,
      error: submitManyValuesByIdError,
      clearError: clearSubmitManyValuesById,
    },
  ] = useSubmitManyValues({
    query:
      "/@batch/statistics/child/attributes/@sourceAttributeName/submit-by-id",
    selectData: (data) => data,
  });

  useEffect(() => {
    if (submitManyValuesByIdError) {
      messageApi.error(submitManyValuesByIdError.message);
      clearSubmitManyValuesById?.();
    }
  }, [submitManyValuesByIdError, messageApi]);

  useEffect(() => {
    if (!isLoadingSubmitManyValuesById) {
      setPendingBtn(null);
    }
  }, [isLoadingSubmitManyValuesById]);

  useEffect(() => {
    if (submitManyValuesByIdData?.result && !isLoadingSubmitManyValuesById) {
      fetchAttributeStatistics();
    }
  }, [isLoadingSubmitManyValuesById]);

  const [
    fetchValidateManyValuesById,
    {
      isLoading: isLoadingValidateManyValuesById,
      data: validateManyValuesByIdData,
      error: validateManyValuesByIdError,
      clearError: clearValidateManyValuesById,
    },
  ] = useValidateManyValues({
    query:
      "/@batch/statistics/child/attributes/@sourceAttributeName/submit-validation-by-id",
    selectData: (data) => data,
  });

  useEffect(() => {
    if (validateManyValuesByIdError) {
      messageApi.error(validateManyValuesByIdError.message);
      clearValidateManyValuesById?.();
    }
  }, [validateManyValuesByIdError, messageApi]);

  useEffect(() => {
    if (!isLoadingValidateManyValuesById) {
      setPendingBtn(null);
    }
  }, [isLoadingValidateManyValuesById]);

  useEffect(() => {
    if (
      validateManyValuesByIdData?.result &&
      !isLoadingValidateManyValuesById
    ) {
      fetchAttributeStatistics();
    }
  }, [isLoadingValidateManyValuesById, messageApi]);

  useEffect(() => {
    if (valuesFromHashData?.length) {
      const y =
        sourceValuesTableRef.current.getBoundingClientRect().top +
        window.scrollY -
        64 -
        16;
      window.scrollTo({ top: y, behavior: "smooth" });
      setSelectedCheckboxKeys([]);
      setDataForActionWithManyValues([]);
    }
  }, [valuesFromHashData]);

  const columns: ColumnsType = [
    {
      title: "Tag",
      dataIndex: "tag",
      render: (tag) => (
        // <EntityLinksDropdown material={tag} children={<StyledTableItemLink><a>{tag}</a></StyledTableItemLink>} />
        <p>{tag}</p>
      ),
    },
    {
      title: "Classification",
      dataIndex: "class",
    },
    {
      title: "Tag Description",
      dataIndex: "description",
    },
    {
      title: "JDE",
      dataIndex: "jde",
      render: (value) => value && <SourceTag source={"llm"} text={value} />,
    },
    {
      title: "EP",
      dataIndex: "ep",
      render: (value) => value && <SourceTag source={"llm"} text={value} />,
    },
    {
      title: "SPI",
      dataIndex: "spi",
      render: (value) => value && <SourceTag source={"llm"} text={value} />,
    },
    {
      title: "Verified",
      dataIndex: "final",
      render: (final) => final && <SourceTag source={"finals"} text={final} />,
    },
    {
      title: "Actions",
      dataIndex: "action",
      render: (_: unknown, record) => {
        const field = getBatchField(currentBatch);
        const valueForBatch = field != null ? record[field] : undefined;
        return (
          <SubmitActions
            id={record.id}
            value={record.final ?? valueForBatch}
            fetchAttributeStatistics={fetchAttributeStatistics}
            state={parentRowSourceValuesParams.state ?? undefined}
          />
        );
      },
    },
  ];

  const rowSelection: TableRowSelection<any> = {
    selectedRowKeys: selectedCheckboxKeys,
    onChange: (newKeys) => {
      setSelectedCheckboxKeys(newKeys);

      const field = getBatchField(curParentBatch);

      setDataForActionWithManyValues(() =>
        (valuesFromHashData ?? [])
          .filter((r) => newKeys.includes(r.id))
          .map((r) => {
            const valueForBatch = field != null ? r[field] : undefined;
            const value = r.final ?? valueForBatch;
            return {
              id: r.id,
              target_value: value,
              source_value: value,
            };
          }),
      );
    },
  };

  const submitManyById = (actions: Array<Record<string, unknown>>) => {
    fetchSubmitManyValuesById(
      {
        batch: curParentBatch,
        sourceAttributeName: attributeToAnalyze,
      },
      {
        child_batch: curParentBatch,
        actions,
      },
    );
  };

  const validateManyById = (actions: Array<Record<string, unknown>>) => {
    fetchValidateManyValuesById(
      {
        batch: curParentBatch,
        sourceAttributeName: attributeToAnalyze,
      },
      {
        child_batch: curParentBatch,
        actions,
      },
    );
  };

  return (
    <>
      <SpinWrapper>
        <Spin
          indicator={<LoadingOutlined />}
          spinning={isLoadingValuesFromHashData}
        />
      </SpinWrapper>

      <StyledSourceValuesTableWrapper ref={sourceValuesTableRef}>
          <StyledTable
            className="primary-table"
            tableLayout="auto"
            rowKey="id"
            rowSelection={rowSelection}
            columns={columns}
            dataSource={valuesFromHashData ?? []}
            onChange={(pagination) => {
              if (pagination.pageSize !== pageSize) {
                setPageSize(pagination.pageSize);
                return;
              }
              valuesFromHashLoadMore(pagination.current);
            }}
            pagination={{
              disabled: Boolean(selectedCheckboxKeys?.length) || disabled,
              current: page,
              pageSize: pageSize,
              total: valuesFromHashTableTotalRows,
              showSizeChanger: true,
              pageSizeOptions: ["15", "25", "50", "100"],
              showTotal: () => (
                <AgentTableToolbar
                  mode="SourceValuesTable"
                  parentState={parentRowSourceValuesParams.state}
                  rows={dataForActionWithManyValues}
                  disabled={disabled}
                  pendingBtn={pendingBtn}
                  setPendingBtn={setPendingBtn}
                  isLoadingSubmit={isLoadingSubmitManyValuesById}
                  isLoadingValidate={isLoadingValidateManyValuesById}
                  showOverride={parentRowSourceValuesParams.state === "llm"}
                  warningCount={dataForActionWithManyValues.length}
                  onSubmit={submitManyById}
                  onValidate={validateManyById}
                />
              ),
            }}
          />
      </StyledSourceValuesTableWrapper>
    </>
  );
};

export default ReviewSourceValuesTable;
